import {useEffect, useState} from 'react';
import {Button, Form, InputGroup, Modal} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import {WlanBand, WlanInterface, WlanMode} from '../../Types/routerForms';
import {PASSWORD_REGEX, randomStrongPassword} from '../../Utils/random';

interface EditWifiModalProps {
  show: boolean;
  onHide: () => void;
  initial: WlanInterface | null;
  band: WlanBand;
  onSave: (next: WlanInterface) => void;
}

const ALL_MODES: WlanMode[] = ['2ghz', '5ghz', '6ghz'];

const EditWifiModal = ({show, onHide, initial, band, onSave}: EditWifiModalProps): JSX.Element => {
  const intl = useIntl();
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [active, setActive] = useState(true);
  const [mode, setMode] = useState<WlanMode>('2ghz');
  const [showPwd, setShowPwd] = useState(false);
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show && initial) {
      setSsid(initial.ssid);
      setPassword(initial.password);
      setActive(initial.active);
      setMode(initial.mode);
      setShowPwd(false);
      setValidated(false);
    }
  }, [show, initial]);

  if (!initial) return <></>;

  const isSsidValid = ssid.trim().length > 0;
  const isPasswordValid = PASSWORD_REGEX.test(password);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidated(true);
    if (!isSsidValid || !isPasswordValid) return;
    onSave({...initial, ssid, password, active, mode});
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.EDIT_TITLE'})}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit} id="editWifiForm">
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.SSID'})}
            </Form.Label>
            <Form.Control
              type="text"
              value={ssid}
              onChange={(e) => setSsid(e.target.value)}
              isInvalid={validated && !isSsidValid}
              required
            />
            <Form.Control.Feedback type="invalid">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.REQUIRED'})}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.PASSWORD'})}
            </Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                isInvalid={validated && !isPasswordValid}
                required
              />
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => setShowPwd((s) => !s)}
              >
                <i className={`fa-solid ${showPwd ? 'fa-eye-slash' : 'fa-eye'}`} />
              </Button>
              <Button
                variant="outline-secondary"
                type="button"
                onClick={() => setPassword(randomStrongPassword(12))}
                title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.RANDOM_PWD'})}
              >
                <i className="fa-solid fa-dice" />
              </Button>
              <Form.Control.Feedback type="invalid">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.VALIDATION.PASSWORD'})}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.PASSWORD_HELP'})}
            </Form.Text>
          </Form.Group>
          {band === '5GHz' && (
            <Form.Group className="mb-3">
              <Form.Label>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.MODE'})}</Form.Label>
              <Form.Select value={mode} onChange={(e) => setMode(e.target.value as WlanMode)}>
                {ALL_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m.toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group>
            <Form.Label>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.STATUS'})}</Form.Label>
            <Form.Select
              value={active ? 'on' : 'off'}
              onChange={(e) => setActive(e.target.value === 'on')}
            >
              <option value="on">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.STATUS_ON'})}
              </option>
              <option value="off">
                {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.STATUS_OFF'})}
              </option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage({id: 'COMMON.CANCEL'})}
          </Button>
          <Button variant="primary" type="submit" form="editWifiForm">
            {intl.formatMessage({id: 'COMMON.SAVE'})}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditWifiModal;
